import { useEffect, useMemo, useState } from 'react';

// Remember successful URLs during the current browser page session.
const successfulImageCache = new Map();

const IMAGE_HOSTS = [
    'localhost',
    'new.fgh.fun',
    'new.fgh.cool',
    'new.thefgh.fun',
];

const buildImageUrls = (gameId, gameImg) => {
    const urls = [];

    if (typeof gameImg === 'string' && gameImg.trim()) {
        urls.push(gameImg.trim());
    }

    if (!gameId) {
        return [...new Set(urls)];
    }

    const hostname = window.location.hostname;

    const imageHost = IMAGE_HOSTS.includes(hostname)
        ? 'https://fgh.zone'
        : window.location.origin;

    const id = String(gameId);

    [2, 3].forEach((length) => {
        const folder = id.substring(0, length);

        const baseUrl = `${imageHost}/json/resources/images/WLP/${folder}/${id}`;

        const dimensionUrl = `${imageHost}/json/resources/images/WLP/${folder}/200x200/${id}`;

        [baseUrl, dimensionUrl].forEach((url) => {
            ['jpg', 'png', 'gif'].forEach((extension) => {
                urls.push(`${url}.${extension}`);
            });
        });
    });

    return [...new Set(urls)];
};

const GameImage = ({ gameId, gameImg }) => {
    const cacheKey = `${gameId ?? ''}|${gameImg ?? ''}`;

    const imageUrls = useMemo(
        () => buildImageUrls(gameId, gameImg),
        [gameId, gameImg]
    );

    const orderedUrls = useMemo(() => {
        const cachedUrl = successfulImageCache.get(cacheKey);

        if (cachedUrl && imageUrls.includes(cachedUrl)) {
            return [cachedUrl, ...imageUrls.filter((url) => url !== cachedUrl)];
        }

        return imageUrls;
    }, [cacheKey, imageUrls]);

    const [imageState, setImageState] = useState({
        key: cacheKey,
        index: 0,
        loaded: false,
    });

    const state =
        imageState.key === cacheKey
            ? imageState
            : { key: cacheKey, index: 0, loaded: false };

    useEffect(() => {
        setImageState({
            key: cacheKey,
            index: 0,
            loaded: false,
        });
    }, [cacheKey]);

    const currentUrl = orderedUrls[state.index];

    const handleImageLoad = () => {
        if (!currentUrl) return;

        successfulImageCache.set(cacheKey, currentUrl);

        setImageState((prev) => {
            if (prev.key !== cacheKey || prev.index !== state.index) {
                return prev;
            }

            return { ...prev, loaded: true };
        });
    };

    const handleImageError = () => {
        if (successfulImageCache.get(cacheKey) === currentUrl) {
            successfulImageCache.delete(cacheKey);
        }

        setImageState((prev) => {
            if (prev.key !== cacheKey || prev.index !== state.index) {
                return prev;
            }

            return {
                ...prev,
                index: prev.index + 1,
                loaded: false,
            };
        });
    };

    if (!currentUrl) {
        return (
            <div
                className="game-image"
                role="img"
                aria-label="Game image unavailable"
            />
        );
    }

    return (
        <img
            key={`${cacheKey}-${state.index}`}
            src={currentUrl}
            alt="Game"
            loading="lazy"
            decoding="async"
            onLoad={handleImageLoad}
            onError={handleImageError}
            className={`game-image ${state.loaded ? 'loaded' : ''}`}
        />
    );
};

export default GameImage;
